// DESIGN_AUDIT Phase 11A fixes applied:
//   FIX 1: Replaced 4 occurrences of var(--bg-card) with var(--bg-elevated) — --bg-card was undefined
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Calendar, ChevronLeft, ChevronRight, X,
  Wrench, ClipboardList, Users, Star, LayoutGrid, AlignJustify, List, ChevronDown, Pencil, Edit2, Trash,
} from 'lucide-react';
import { api } from '../api/client.ts';
import { CalendarEvent, DirectorTask } from '../types/index.ts';
import PageFocusToggle from '../components/PageFocusToggle.tsx';
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, format, isSameMonth, isSameDay, addMonths, subMonths,
  addWeeks, subWeeks, startOfDay, getHours, isAfter, isBefore, addDays,
} from 'date-fns';

function parseLocalDate(dateStr: string): Date {
  const tIdx = dateStr.indexOf('T');
  if (tIdx === -1) {
    const parts = dateStr.split('-');
    return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
  }
  const dateParts = dateStr.slice(0, tIdx).split('-');
  const timeParts = dateStr.slice(tIdx + 1).split(':');
  const y = parseInt(dateParts[0], 10);
  const m = parseInt(dateParts[1], 10);
  const d = parseInt(dateParts[2], 10);
  const h = parseInt(timeParts[0], 10);
  const min = parseInt(timeParts[1], 10);
  const s = timeParts[2] ? parseInt(timeParts[2], 10) : 0;
  return new Date(y, m - 1, d, h, min, s);
}

// ─── Types & Constants ────────────────────────────────────────────────────────

const DEFAULT_EVENT_TYPE_COLORS: Record<string, string> = {
  'Preventive Maintenance': '#3b82f6',
  'Inspection':             '#10b981',
  'Repair':                 '#ef4444',
  'Vendor Visit':           '#f59e0b',
  'Task Due':               '#8b5cf6',
  'Meeting':                '#06b6d4',
  // legacy names kept for backward compat
  'Maintenance': '#3b82f6',
  'Task':        '#f97316',
  'Reminder':    '#06b6d4',
  'Custom':      '#6b7280',
};

const DEFAULT_EVENT_TYPES = [
  'Preventive Maintenance', 'Inspection', 'Repair',
  'Vendor Visit', 'Task Due', 'Meeting',
];

const REPEAT_OPTIONS = ['Daily', 'Weekly', 'Monthly', 'Yearly'];
const HOURS = Array.from({ length: 16 }, (_, i) => i + 6); // 6 AM – 9 PM

function getEventTypeColor(typeName: string): string {
  return DEFAULT_EVENT_TYPE_COLORS[typeName] ?? '#6b7280';
}

function eventColor(event: CalendarEvent, now: Date): string {
  if (event.event_color && event.event_color !== '#3b82f6') return event.event_color;
  const color = getEventTypeColor(event.event_type);
  // Overdue: past date for task-like types
  if (!isAfter(parseLocalDate(event.start_date), now)) {
    if (event.event_type === 'Task' || event.event_type === 'Task Due' ||
        event.event_type === 'Maintenance' || event.event_type === 'Repair') {
      return '#ef4444';
    }
  }
  return color;
}

function taskToEvent(task: DirectorTask): CalendarEvent {
  return {
    id: -task.id,
    facility_id: task.facility_id,
    title: task.title,
    description: task.description,
    start_date: task.due_date ?? task.created_at,
    end_date: null,
    is_all_day: true,
    event_type: 'Task',
    event_color: task.is_completed ? '#22c55e' : '#f97316',
    is_recurring: false,
    recurrence_pattern: null,
    has_reminder: false,
    reminder_days_before: 0,
    director_task_id: task.id,
    vendor_id: null,
  };
}

// ─── EventPill ────────────────────────────────────────────────────────────────

function EventPill({ event, onClick }: { event: CalendarEvent; onClick: () => void }) {
  const now = new Date();
  const color = eventColor(event, now);
  return (
    <div onClick={e => { e.stopPropagation(); onClick(); }} style={{
      background: `${color}22`, border: `1px solid ${color}55`,
      borderLeft: `3px solid ${color}`,
      borderRadius: 3, padding: '1px 5px', fontSize: '0.6875rem',
      color: color, marginBottom: 2,
      overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
      cursor: 'pointer', userSelect: 'none',
    }}
      title={event.title}>
      {event.title}
    </div>
  );
}

// ─── Event Detail Popup ───────────────────────────────────────────────────────

function EventDetailPopup({ event, onClose, onDelete }:
  { event: CalendarEvent; onClose: () => void; onDelete: (id: number) => void }) {
  const color = eventColor(event, new Date());
  const isTask = event.id < 0;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 380 }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, display: 'inline-block' }} />
            <h3 className="modal-title" style={{ margin: 0 }}>{event.title}</h3>
          </div>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: '0.875rem' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <span style={{ color: 'var(--text-muted)', minWidth: 80 }}>Date:</span>
            <span>{format(parseLocalDate(event.start_date), 'MMMM d, yyyy')}</span>
          </div>
          {!event.is_all_day && (
            <div style={{ display: 'flex', gap: 8 }}>
              <span style={{ color: 'var(--text-muted)', minWidth: 80 }}>Time:</span>
              <span>{format(parseLocalDate(event.start_date), 'h:mm a')}{event.end_date ? ` – ${format(parseLocalDate(event.end_date), 'h:mm a')}` : ''}</span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <span style={{ color: 'var(--text-muted)', minWidth: 80 }}>Type:</span>
            <span className="badge" style={{ background: `${color}22`, color, border: `1px solid ${color}44` }}>{event.event_type}</span>
          </div>
          {event.is_recurring && (
            <div style={{ display: 'flex', gap: 8 }}>
              <span style={{ color: 'var(--text-muted)', minWidth: 80 }}>Repeat:</span>
              <span>{event.recurrence_pattern}</span>
            </div>
          )}
          {(event.description || (event as CalendarEvent & { notes?: string }).notes) && (
            <div style={{ color: 'var(--text-secondary)', paddingTop: 4, borderTop: '1px solid var(--border-subtle)' }}>
              {event.description ?? (event as CalendarEvent & { notes?: string }).notes}
            </div>
          )}
          {!isTask && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 4 }}>
              <button className="btn btn-ghost btn-sm" style={{ color: 'var(--accent-danger)' }}
                onClick={() => { onDelete(event.id); onClose(); }}>
                Delete
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── EventTypeSelect — custom single-select dropdown ─────────────────────────

function EventTypeSelect({ value, onChange, eventTypes }: {
  value: string;
  onChange: (v: string) => void;
  eventTypes: string[];
}) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function openDropdown() {
    if (ref.current) setRect(ref.current.getBoundingClientRect());
    setOpen(true);
  }

  const color = getEventTypeColor(value);
  const types = eventTypes.length > 0 ? eventTypes : DEFAULT_EVENT_TYPES;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div onClick={openDropdown} style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-input, var(--bg-elevated))',
        cursor: 'pointer', fontSize: '0.8125rem', minHeight: 40,
      }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
        <span style={{ flex: 1, color: 'var(--text-primary)' }}>{value || 'Select type…'}</span>
        <ChevronDown size={14} color="var(--text-muted)" />
      </div>
      {open && rect && createPortal(
        <div style={{
          position: 'fixed', top: rect.bottom + 4, left: rect.left, width: rect.width,
          zIndex: 10000, background: 'var(--bg-elevated)',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)',
          overflow: 'hidden', maxHeight: 220, overflowY: 'auto',
        }}>
          {types.map(type => {
            const tc = getEventTypeColor(type);
            return (
              <div key={type} onClick={() => { onChange(type); setOpen(false); }} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', cursor: 'pointer',
                background: type === value ? 'rgba(59,130,246,0.08)' : 'none',
                fontSize: '0.8125rem', color: 'var(--text-primary)',
              }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover)')}
                onMouseLeave={e => (e.currentTarget.style.background = type === value ? 'rgba(59,130,246,0.08)' : 'none')}
              >
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: tc, flexShrink: 0 }} />
                {type}
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Add Event Modal ──────────────────────────────────────────────────────────

function AddEventModal({ defaultDate, onClose, onSave, eventTypes }:
  { defaultDate: Date; onClose: () => void; onSave: (data: Partial<CalendarEvent>) => Promise<void>; eventTypes: string[] }) {
  const types = eventTypes.length > 0 ? eventTypes : DEFAULT_EVENT_TYPES;
  const today = format(defaultDate, 'yyyy-MM-dd');
  const [title, setTitle]       = useState('');
  const [date, setDate]         = useState(today);
  const [time, setTime]         = useState('');
  const [endTime, setEndTime]   = useState('');
  const [isAllDay, setIsAllDay] = useState(true);
  const [eventType, setEventType] = useState(types[0] ?? 'Meeting');
  const [notes, setNotes]       = useState('');
  const [recurring, setRecurring] = useState(false);
  const [frequency, setFrequency] = useState('Weekly');
  const [saving, setSaving]     = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title || !date) return;
    setSaving(true);
    try {
      const startDate = time && !isAllDay
        ? new Date(`${date}T${time}`).toISOString()
        : new Date(date).toISOString();
      const endDate = endTime && !isAllDay
        ? new Date(`${date}T${endTime}`).toISOString()
        : null;
      await onSave({
        title, start_date: startDate, end_date: endDate,
        is_all_day: isAllDay, event_type: eventType,
        event_color: getEventTypeColor(eventType),
        description: notes || null, notes: notes || null,
        is_recurring: recurring,
        recurrence_pattern: recurring ? frequency : null,
      } as Partial<CalendarEvent>);
    } finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <h3 className="modal-title"><Plus size={16} style={{ marginRight: 8 }} />Add Event</h3>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="form-group">
            <label className="form-label">Title *</label>
            <input className="form-input" required value={title} onChange={e => setTitle(e.target.value)} autoFocus />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Date *</label>
              <input className="form-input" type="date" required value={date} onChange={e => setDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Event Type</label>
              <EventTypeSelect value={eventType} onChange={setEventType} eventTypes={types} />
            </div>
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.875rem', marginBottom: 8 }}>
              <input type="checkbox" checked={isAllDay} onChange={e => setIsAllDay(e.target.checked)} />
              All-day event
            </label>
            {!isAllDay && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Start Time</label>
                  <input className="form-input" type="time" value={time} onChange={e => setTime(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">End Time</label>
                  <input className="form-input" type="time" value={endTime} onChange={e => setEndTime(e.target.value)} />
                </div>
              </div>
            )}
          </div>

          {/* Recurring toggle */}
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.875rem', marginBottom: recurring ? 10 : 0 }}>
              <input type="checkbox" checked={recurring} onChange={e => setRecurring(e.target.checked)} />
              Recurring event
            </label>
            {recurring && (
              <div style={{ display: 'flex', gap: 6 }}>
                {REPEAT_OPTIONS.map(opt => (
                  <button key={opt} type="button" onClick={() => setFrequency(opt)} style={{
                    padding: '5px 12px', borderRadius: 'var(--radius-sm)',
                    border: '1px solid',
                    borderColor: frequency === opt ? 'var(--accent-primary)' : 'var(--border-default)',
                    background: frequency === opt ? 'rgba(59,130,246,0.12)' : 'var(--bg-elevated)',
                    color: frequency === opt ? 'var(--accent-primary)' : 'var(--text-secondary)',
                    fontSize: '0.8125rem', cursor: 'pointer', fontWeight: frequency === opt ? 600 : 400,
                  }}>
                    {opt}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-textarea" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Add Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Edit Event Modal ─────────────────────────────────────────────────────────

function EditEventModal({ event, onClose, onSave, onDelete, eventTypes }:
  { event: CalendarEvent; onClose: () => void; onSave: (data: Partial<CalendarEvent>) => Promise<void>; onDelete: () => Promise<void>; eventTypes: string[] }) {
  const types = eventTypes.length > 0 ? eventTypes : DEFAULT_EVENT_TYPES;
  const parsed = parseLocalDate(event.start_date);

  const [title, setTitle]         = useState(event.title);
  const [date, setDate]           = useState(format(parsed, 'yyyy-MM-dd'));
  const [time, setTime]           = useState(event.is_all_day ? '' : format(parsed, 'HH:mm'));
  const [endTime, setEndTime]     = useState(event.end_date ? format(parseLocalDate(event.end_date), 'HH:mm') : '');
  const [isAllDay, setIsAllDay]   = useState(event.is_all_day);
  const [eventType, setEventType] = useState(event.event_type);
  const [notes, setNotes]         = useState((event.description ?? (event as CalendarEvent & { notes?: string }).notes) ?? '');
  const [recurring, setRecurring] = useState(event.is_recurring);
  const [frequency, setFrequency] = useState(event.recurrence_pattern ?? 'Weekly');
  const [saving, setSaving]       = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting]   = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title || !date) return;
    setSaving(true);
    try {
      const startDate = time && !isAllDay
        ? new Date(`${date}T${time}`).toISOString()
        : new Date(date).toISOString();
      const endDate = endTime && !isAllDay
        ? new Date(`${date}T${endTime}`).toISOString()
        : null;
      await onSave({
        title, start_date: startDate, end_date: endDate,
        is_all_day: isAllDay, event_type: eventType,
        event_color: getEventTypeColor(eventType),
        description: notes || null, notes: notes || null,
        is_recurring: recurring,
        recurrence_pattern: recurring ? frequency : null,
      } as Partial<CalendarEvent>);
    } finally { setSaving(false); }
  }

  async function handleDelete() {
    setDeleting(true);
    try { await onDelete(); } finally { setDeleting(false); }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <h3 className="modal-title"><Pencil size={15} style={{ marginRight: 8 }} />Edit Event</h3>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="form-group">
            <label className="form-label">Title *</label>
            <input className="form-input" required value={title} onChange={e => setTitle(e.target.value)} autoFocus />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Date *</label>
              <input className="form-input" type="date" required value={date} onChange={e => setDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Event Type</label>
              <EventTypeSelect value={eventType} onChange={setEventType} eventTypes={types} />
            </div>
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.875rem', marginBottom: 8 }}>
              <input type="checkbox" checked={isAllDay} onChange={e => setIsAllDay(e.target.checked)} />
              All-day event
            </label>
            {!isAllDay && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Start Time</label>
                  <input className="form-input" type="time" value={time} onChange={e => setTime(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">End Time</label>
                  <input className="form-input" type="time" value={endTime} onChange={e => setEndTime(e.target.value)} />
                </div>
              </div>
            )}
          </div>

          {/* Recurring toggle */}
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.875rem', marginBottom: recurring ? 10 : 0 }}>
              <input type="checkbox" checked={recurring} onChange={e => setRecurring(e.target.checked)} />
              Recurring event
            </label>
            {recurring && (
              <div style={{ display: 'flex', gap: 6 }}>
                {REPEAT_OPTIONS.map(opt => (
                  <button key={opt} type="button" onClick={() => setFrequency(opt)} style={{
                    padding: '5px 12px', borderRadius: 'var(--radius-sm)',
                    border: '1px solid',
                    borderColor: frequency === opt ? 'var(--accent-primary)' : 'var(--border-default)',
                    background: frequency === opt ? 'rgba(59,130,246,0.12)' : 'var(--bg-elevated)',
                    color: frequency === opt ? 'var(--accent-primary)' : 'var(--text-secondary)',
                    fontSize: '0.8125rem', cursor: 'pointer', fontWeight: frequency === opt ? 600 : 400,
                  }}>
                    {opt}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-textarea" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
          </div>

          {/* Footer: delete left, cancel + save right */}
          <div style={{ display: 'flex', alignItems: 'center', paddingTop: 4 }}>
            {/* Delete / confirm */}
            {!confirmDelete ? (
              <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--accent-danger)' }}
                onClick={() => setConfirmDelete(true)}>
                Delete
              </button>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Delete this event?</span>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setConfirmDelete(false)}>No</button>
                <button type="button" className="btn btn-sm" disabled={deleting}
                  style={{ background: 'var(--accent-danger)', color: '#fff', border: 'none', cursor: 'pointer', padding: '5px 12px', borderRadius: 'var(--radius-sm)', fontSize: '0.8125rem' }}
                  onClick={handleDelete}>
                  {deleting ? 'Deleting…' : 'Yes, delete'}
                </button>
              </div>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Upcoming View ────────────────────────────────────────────────────────────

function UpcomingView({ allEvents, eventTypeOptions, onEventClick, onEditEvent }: {
  allEvents: CalendarEvent[];
  eventTypeOptions: string[];
  onEventClick: (e: CalendarEvent) => void;
  onEditEvent: (e: CalendarEvent) => void;
}) {
  const queryClient = useQueryClient();
  const today = startOfDay(new Date());
  const [miniCalMonth, setMiniCalMonth] = useState(startOfMonth(today));
  const [fromDate, setFromDate] = useState(today);
  const [removedIds, setRemovedIds] = useState<Set<number>>(new Set());
  const listRef = useRef<HTMLDivElement>(null);

  const monthStart = startOfMonth(miniCalMonth);
  const calDays = eachDayOfInterval({ start: startOfWeek(monthStart), end: endOfWeek(endOfMonth(miniCalMonth)) });

  function getEventsForDay(day: Date) {
    return allEvents.filter(e => isSameDay(parseLocalDate(e.start_date), day));
  }

  async function handleDeleteEvent(evt: React.MouseEvent, event: CalendarEvent) {
    evt.stopPropagation();
    if (!window.confirm('Delete this event?')) return;
    if (event.id > 0) {
      await api.delete(`/schedule/events/${event.id}`);
    } else {
      await api.delete(`/tasks/${Math.abs(event.id)}`);
    }
    queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
    queryClient.invalidateQueries({ queryKey: ['director-tasks'] });
  }

  // Upcoming: events from fromDate forward, sorted ascending
  const upcoming = allEvents
    .filter(e => !removedIds.has(e.id) && !isBefore(parseLocalDate(e.start_date), fromDate))
    .sort((a, b) => parseLocalDate(a.start_date).getTime() - parseLocalDate(b.start_date).getTime());

  // Group by date string
  const groups: { dateKey: string; date: Date; events: CalendarEvent[] }[] = [];
  upcoming.forEach(e => {
    const dateKey = format(parseLocalDate(e.start_date), 'yyyy-MM-dd');
    const last = groups[groups.length - 1];
    if (last && last.dateKey === dateKey) {
      last.events.push(e);
    } else {
      groups.push({ dateKey, date: startOfDay(parseLocalDate(e.start_date)), events: [e] });
    }
  });

  function handleDayClick(day: Date) {
    setFromDate(startOfDay(day));
    if (listRef.current) listRef.current.scrollTop = 0;
  }

  const legendTypes = eventTypeOptions.length > 0 ? eventTypeOptions : DEFAULT_EVENT_TYPES;

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>

      {/* ── LEFT: Mini Calendar ── */}
      <div style={{
        width: 300, flexShrink: 0,
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-lg)',
        padding: 16,
      }}>
        {/* Month header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <button className="btn btn-ghost btn-sm" style={{ padding: 4 }}
            onClick={() => setMiniCalMonth(m => subMonths(m, 1))}>
            <ChevronLeft size={14} />
          </button>
          <span style={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--text-primary)' }}>
            {format(miniCalMonth, 'MMMM yyyy')}
          </span>
          <button className="btn btn-ghost btn-sm" style={{ padding: 4 }}
            onClick={() => setMiniCalMonth(m => addMonths(m, 1))}>
            <ChevronRight size={14} />
          </button>
        </div>

        {/* Day-of-week headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 2 }}>
          {['S','M','T','W','T','F','S'].map((d, i) => (
            <div key={i} style={{
              textAlign: 'center', fontSize: '0.6875rem', fontWeight: 600,
              color: 'var(--text-muted)', paddingBottom: 4,
            }}>{d}</div>
          ))}
        </div>

        {/* Calendar days */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', rowGap: 2 }}>
          {calDays.map(day => {
            const isToday   = isSameDay(day, today);
            const isCurrent = isSameMonth(day, miniCalMonth);
            const isSelected = isSameDay(day, fromDate);
            const dayEvts   = getEventsForDay(day);
            const dotColors = Array.from(new Set(dayEvts.map(e => getEventTypeColor(e.event_type)))).slice(0, 3);

            return (
              <div key={day.toISOString()} onClick={() => handleDayClick(day)} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                paddingBottom: 2, cursor: 'pointer',
                opacity: isCurrent ? 1 : 0.28,
                borderRadius: 6,
              }}>
                <div style={{
                  width: 30, height: 30, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.8125rem',
                  fontWeight: isToday ? 700 : 400,
                  background: isToday ? '#3b82f6' : isSelected && !isToday ? 'rgba(59,130,246,0.15)' : 'transparent',
                  color: isToday ? '#fff' : isSelected && !isToday ? '#3b82f6' : 'var(--text-primary)',
                  transition: 'background 0.12s',
                }}>
                  {format(day, 'd')}
                </div>
                {dotColors.length > 0 && (
                  <div style={{ display: 'flex', gap: 2, marginTop: 1 }}>
                    {dotColors.map((color, i) => (
                      <div key={i} style={{ width: 4, height: 4, borderRadius: '50%', background: color }} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Color legend */}
        <div style={{
          marginTop: 16, paddingTop: 14,
          borderTop: '1px solid var(--border-subtle)',
          display: 'flex', flexDirection: 'column', gap: 7,
        }}>
          {legendTypes.map(type => (
            <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: getEventTypeColor(type), flexShrink: 0 }} />
              <span>{type}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── RIGHT: Upcoming Event List ── */}
      <div ref={listRef} style={{ flex: 1, overflowY: 'auto', maxHeight: 'calc(100vh - 230px)' }}>
        <h3 style={{ marginBottom: 16, fontSize: '0.9375rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
          Upcoming from {format(fromDate, 'MMMM d, yyyy')}
        </h3>

        {groups.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '48px 24px',
            color: 'var(--text-muted)',
            background: 'var(--bg-elevated)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border-subtle)',
          }}>
            <Calendar size={32} style={{ opacity: 0.3, marginBottom: 8, display: 'block', margin: '0 auto 8px' }} />
            <div style={{ fontWeight: 500, marginBottom: 4 }}>No upcoming events</div>
            <div style={{ fontSize: '0.8125rem' }}>Events from {format(fromDate, 'MMM d')} forward will appear here</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {groups.map(group => (
              <div key={group.dateKey}>
                {/* Date divider */}
                <div style={{
                  fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)',
                  textTransform: 'uppercase', letterSpacing: '0.07em',
                  marginBottom: 8, paddingLeft: 4,
                }}>
                  {isSameDay(group.date, today)
                    ? 'Today'
                    : isSameDay(group.date, addDays(today, 1))
                    ? 'Tomorrow'
                    : format(group.date, 'EEEE, MMMM d')}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {group.events.map(event => {
                    const color = getEventTypeColor(event.event_type);
                    const eventDate = parseLocalDate(event.start_date);
                    const isRealEvent = true;
                    return (
                      <div key={event.id} onClick={() => onEventClick(event)} style={{
                        display: 'flex', alignItems: 'stretch',
                        background: 'var(--bg-elevated)',
                        border: '1px solid var(--border-subtle)',
                        borderLeft: `4px solid ${color}`,
                        borderRadius: 'var(--radius-md)',
                        overflow: 'hidden', cursor: 'pointer',
                        transition: 'background 0.12s',
                      }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'var(--bg-elevated)')}
                      >
                        {/* Day / month badge */}
                        <div style={{
                          minWidth: 56, display: 'flex', flexDirection: 'column',
                          alignItems: 'center', justifyContent: 'center',
                          padding: '12px 8px',
                          borderRight: '1px solid var(--border-subtle)',
                        }}>
                          <div style={{ fontSize: '1.25rem', fontWeight: 700, lineHeight: 1, color: 'var(--text-primary)' }}>
                            {format(eventDate, 'd')}
                          </div>
                          <div style={{ fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.04em' }}>
                            {format(eventDate, 'MMM')}
                          </div>
                        </div>
                        {/* Event details */}
                        <div style={{ flex: 1, padding: '10px 14px' }}>
                          <div style={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--text-primary)', marginBottom: 3 }}>
                            {event.title}
                          </div>
                          <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 5 }}>
                            {!event.is_all_day && (
                              <>
                                <span>{format(eventDate, 'h:mm a')}</span>
                                <span>·</span>
                              </>
                            )}
                            <span style={{ color, fontWeight: 500 }}>{event.event_type}</span>
                          </div>
                        </div>
                        {/* Edit and delete buttons for real calendar events */}
                        {/* Edit + Delete buttons — real events only */}
                        {isRealEvent && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto', marginRight: 12, flexShrink: 0, alignSelf: 'center' }}>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); onEditEvent(event); }}
                              style={{ border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: '4px 8px', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}
                              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
                              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                              title="Edit event"
                            >
                              <Edit2 size={14} />
                            </button>
                              <button
                                type="button"
                                onClick={(e) => handleDeleteEvent(e, event)}
                                style={{ border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: '4px 8px', background: 'transparent', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center' }}
                                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.borderColor = '#ef4444'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}
                                title="Delete event"
                              >
                                <Trash size={14} />
                              </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Month View ───────────────────────────────────────────────────────────────

function MonthView({ currentDate, events, tasks, onDayClick, onEventClick }:
  { currentDate: Date; events: CalendarEvent[]; tasks: DirectorTask[]; onDayClick: (d: Date) => void; onEventClick: (e: CalendarEvent) => void }) {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calStart = startOfWeek(monthStart);
  const calEnd = endOfWeek(monthEnd);
  const calDays = eachDayOfInterval({ start: calStart, end: calEnd });
  const now = new Date();

  const taskEvents = tasks.filter(t => t.due_date).map(taskToEvent);
  const allEvents = [...events, ...taskEvents];

  function getDay(day: Date) {
    return allEvents.filter(e => isSameDay(parseLocalDate(e.start_date), day));
  }

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid var(--border-subtle)' }}>
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
          <div key={d} style={{ padding: '10px 12px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', textAlign: 'center' }}>{d}</div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {calDays.map(day => {
          const isToday = isSameDay(day, now);
          const isCurrent = isSameMonth(day, currentDate);
          const dayEvents = getDay(day);
          return (
            <div key={day.toISOString()} onClick={() => onDayClick(day)} style={{
              minHeight: 90, padding: '8px 10px',
              borderRight: '1px solid var(--border-subtle)',
              borderBottom: '1px solid var(--border-subtle)',
              opacity: isCurrent ? 1 : 0.35, cursor: 'pointer',
              transition: 'background var(--transition)',
            }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
              <div style={{
                width: 26, height: 26, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.8125rem', fontWeight: isToday ? 700 : 400,
                background: isToday ? 'var(--accent-primary)' : 'transparent',
                color: isToday ? '#fff' : 'var(--text-primary)',
                marginBottom: 4,
              }}>
                {format(day, 'd')}
              </div>
              {dayEvents.slice(0, 3).map(event => (
                <EventPill key={event.id} event={event} onClick={() => onEventClick(event)} />
              ))}
              {dayEvents.length > 3 && (
                <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>+{dayEvents.length - 3} more</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Week View ────────────────────────────────────────────────────────────────

function WeekView({ currentDate, events, tasks, onSlotClick, onEventClick }:
  { currentDate: Date; events: CalendarEvent[]; tasks: DirectorTask[]; onSlotClick: (d: Date) => void; onEventClick: (e: CalendarEvent) => void }) {
  const weekStart = startOfWeek(currentDate);
  const weekDays = eachDayOfInterval({ start: weekStart, end: endOfWeek(currentDate) });
  const now = new Date();

  const taskEvents = tasks.filter(t => t.due_date).map(taskToEvent);
  const allEvents = [...events, ...taskEvents];

  function getAllDay(day: Date) {
    return allEvents.filter(e => e.is_all_day && isSameDay(parseLocalDate(e.start_date), day));
  }
  function getHourEvents(day: Date, hour: number) {
    return allEvents.filter(e => {
      if (e.is_all_day) return false;
      const d = parseLocalDate(e.start_date);
      return isSameDay(d, day) && getHours(d) === hour;
    });
  }

  const colStyle: React.CSSProperties = { borderRight: '1px solid var(--border-subtle)', borderBottom: '1px solid var(--border-subtle)', padding: '2px 4px', minHeight: 48 };

  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ minWidth: 640 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '56px repeat(7, 1fr)', borderBottom: '2px solid var(--border-default)' }}>
          <div style={{ borderRight: '1px solid var(--border-subtle)' }} />
          {weekDays.map(day => {
            const isToday = isSameDay(day, now);
            return (
              <div key={day.toISOString()} style={{
                textAlign: 'center', padding: '8px 4px',
                borderRight: '1px solid var(--border-subtle)',
                background: isToday ? 'rgba(59,130,246,0.06)' : undefined,
              }}>
                <div style={{ fontSize: '0.6875rem', textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em' }}>{format(day, 'EEE')}</div>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', margin: '2px auto 0',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.875rem', fontWeight: isToday ? 700 : 400,
                  background: isToday ? 'var(--accent-primary)' : 'transparent',
                  color: isToday ? '#fff' : 'var(--text-primary)',
                }}>
                  {format(day, 'd')}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '56px repeat(7, 1fr)', borderBottom: '2px solid var(--border-default)' }}>
          <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', padding: '4px 6px', textAlign: 'right', borderRight: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>all-day</div>
          {weekDays.map(day => (
            <div key={day.toISOString()} style={{ ...colStyle, minHeight: 28 }}>
              {getAllDay(day).map(event => (
                <EventPill key={event.id} event={event} onClick={() => onEventClick(event)} />
              ))}
            </div>
          ))}
        </div>

        {HOURS.map(hour => (
          <div key={hour} style={{ display: 'grid', gridTemplateColumns: '56px repeat(7, 1fr)' }}>
            <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', padding: '4px 6px', textAlign: 'right', borderRight: '1px solid var(--border-subtle)', borderBottom: '1px solid var(--border-subtle)', lineHeight: 1 }}>
              {format(new Date(2000, 0, 1, hour), 'h a')}
            </div>
            {weekDays.map(day => {
              const hourEvs = getHourEvents(day, hour);
              return (
                <div key={day.toISOString()} style={{ ...colStyle, cursor: 'pointer' }}
                  onClick={() => { const d = new Date(day); d.setHours(hour); onSlotClick(d); }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  {hourEvs.map(event => (
                    <EventPill key={event.id} event={event} onClick={() => onEventClick(event)} />
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Schedule Page ────────────────────────────────────────────────────────────

type ScheduleView = 'upcoming' | 'month' | 'week';

type DropdownOption = { id: number; dropdown_type: string; value: string; sort_order: number };

export default function SchedulePage() {
  const qc = useQueryClient();
  const [view, setView]               = useState<ScheduleView>('upcoming');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showAddEvent, setShowAddEvent]   = useState(false);
  const [addEventDate, setAddEventDate]   = useState(startOfDay(new Date()));
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [editingEvent, setEditingEvent]   = useState<CalendarEvent | null>(null);

  const today = startOfDay(new Date());

  const monthStart = startOfMonth(currentDate);
  const monthEnd   = endOfMonth(currentDate);
  const weekStart  = startOfWeek(currentDate);
  const weekEnd    = endOfWeek(currentDate);

  // For upcoming: wide window so mini-cal dots work for near-future months
  const upcomingStart = subMonths(today, 1);
  const upcomingEnd   = addMonths(today, 6);

  const rangeStart = view === 'month' ? monthStart : view === 'week' ? weekStart : upcomingStart;
  const rangeEnd   = view === 'month' ? monthEnd   : view === 'week' ? weekEnd   : upcomingEnd;

  const { data: eventsData } = useQuery({
    queryKey: ['calendar-events', format(rangeStart, 'yyyy-MM-dd'), view],
    queryFn: () => api.get<CalendarEvent[]>('/schedule/events', {
      start: rangeStart.toISOString(),
      end: rangeEnd.toISOString(),
    }),
  });
  const { data: tasksData } = useQuery({
    queryKey: ['director-tasks-calendar'],
    queryFn: () => api.get<DirectorTask[]>('/tasks', { status: 'pending', limit: 200 }),
  });
  const { data: eventTypesData } = useQuery({
    queryKey: ['dropdown-event-types'],
    queryFn: () => api.get('/settings/dropdown-options', { category: 'event_types' }),
  });

  const events: CalendarEvent[] = (eventsData as unknown as { data: CalendarEvent[] })?.data ?? [];
  const tasks: DirectorTask[]   = (tasksData as unknown as { data: DirectorTask[] })?.data ?? [];
  const eventTypeOptions: string[] = ((eventTypesData as unknown as { data: DropdownOption[] })?.data ?? []).map(o => o.value);

  // Combine for upcoming view
  const taskEvents = tasks.filter(t => t.due_date).map(taskToEvent);
  const allEvents  = [...events, ...taskEvents];

  const createMut = useMutation({
    mutationFn: (data: Partial<CalendarEvent>) => api.post('/schedule/events', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['calendar-events'] }),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => api.delete(`/schedule/events/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['calendar-events'] }),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CalendarEvent> }) =>
      api.put(`/schedule/events/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['calendar-events'] }),
  });

  function prev() {
    setCurrentDate(d => view === 'month' ? subMonths(d, 1) : subWeeks(d, 1));
  }
  function next() {
    setCurrentDate(d => view === 'month' ? addMonths(d, 1) : addWeeks(d, 1));
  }
  function goToday() { setCurrentDate(new Date()); }

  function handleDayClick(day: Date) {
    setAddEventDate(startOfDay(day));
    setShowAddEvent(true);
  }

  const navLabel = view === 'month'
    ? format(currentDate, 'MMMM yyyy')
    : `${format(startOfWeek(currentDate), 'MMM d')} – ${format(endOfWeek(currentDate), 'MMM d, yyyy')}`;

  const viewBtnStyle = (active: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 5,
    padding: '6px 12px', borderRadius: 'var(--radius-sm)', border: 'none',
    background: active ? 'rgba(59,130,246,0.15)' : 'var(--bg-elevated)',
    color: active ? 'var(--accent-primary)' : 'var(--text-secondary)',
    cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 500,
  });

  return (
    <div>
      {/* Focus mode toggle — same floating-action-cluster (AppLayout.css) + PageFocusToggle
          Dashboard.tsx uses. */}
      <div className="floating-action-cluster"><PageFocusToggle /></div>
      <div className="page-header">
        <div>
          <h2 className="page-title">Calendar</h2>
          <p className="page-subtitle">{events.length} event{events.length !== 1 ? 's' : ''} in range</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setAddEventDate(startOfDay(new Date())); setShowAddEvent(true); }}>
          <Plus size={16} /> Add Event
        </button>
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {/* Navigation — only for month/week */}
        {view !== 'upcoming' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={prev}><ChevronLeft size={16} /></button>
            <h3 style={{ minWidth: 180, textAlign: 'center', margin: 0, fontSize: '1rem' }}>{navLabel}</h3>
            <button className="btn btn-ghost btn-sm" onClick={next}><ChevronRight size={16} /></button>
            <button className="btn btn-ghost btn-sm" onClick={goToday}>Today</button>
          </div>
        )}

        {/* View toggle: Upcoming | Month | Week */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          <button style={viewBtnStyle(view === 'upcoming')} onClick={() => setView('upcoming')}>
            <List size={14} /> Upcoming
          </button>
          <button style={viewBtnStyle(view === 'month')} onClick={() => setView('month')}>
            <LayoutGrid size={14} /> Month
          </button>
          <button style={viewBtnStyle(view === 'week')} onClick={() => setView('week')}>
            <AlignJustify size={14} /> Week
          </button>
        </div>
      </div>

      {/* Legend — only for month/week */}
      {view !== 'upcoming' && (
        <div style={{ display: 'flex', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
          {[
            { label: 'Maintenance', color: '#3b82f6', icon: <Wrench size={11} /> },
            { label: 'Task / Due',  color: '#f97316', icon: <ClipboardList size={11} /> },
            { label: 'Meeting',     color: '#8b5cf6', icon: <Users size={11} /> },
            { label: 'Overdue',     color: '#ef4444', icon: <Star size={11} /> },
          ].map(({ label, color, icon }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              <span style={{ color }}>{icon}</span>
              <span style={{ color }}>{label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Calendar views */}
      {view === 'upcoming' && (
        <UpcomingView
          allEvents={allEvents}
          eventTypeOptions={eventTypeOptions}
          onEventClick={setSelectedEvent}
          onEditEvent={setEditingEvent}
        />
      )}
      {view === 'month' && (
        <MonthView currentDate={currentDate} events={events} tasks={tasks} onDayClick={handleDayClick} onEventClick={setSelectedEvent} />
      )}
      {view === 'week' && (
        <WeekView currentDate={currentDate} events={events} tasks={tasks} onSlotClick={d => { setAddEventDate(d); setShowAddEvent(true); }} onEventClick={setSelectedEvent} />
      )}

      {/* ── Modals ── */}

      {showAddEvent && (
        <AddEventModal
          defaultDate={addEventDate}
          eventTypes={eventTypeOptions}
          onClose={() => setShowAddEvent(false)}
          onSave={async (data) => {
            await createMut.mutateAsync(data);
            setShowAddEvent(false);
          }}
        />
      )}

      {selectedEvent && (
        <EventDetailPopup
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onDelete={id => { deleteMut.mutate(id); setSelectedEvent(null); }}
        />
      )}

      {editingEvent && (
        <EditEventModal
          event={editingEvent}
          eventTypes={eventTypeOptions}
          onClose={() => setEditingEvent(null)}
          onSave={async (data) => {
            await updateMut.mutateAsync({ id: editingEvent.id, data });
            setEditingEvent(null);
          }}
          onDelete={async () => {
            await deleteMut.mutateAsync(editingEvent.id);
            setEditingEvent(null);
          }}
        />
      )}
    </div>
  );
}
